/**
 * useConfigUpload.ts
 * Mirrors useOattUpload.ts pattern exactly.
 * Calls POST /api/config-upload with Bearer token from useSession.
 */

import { useState, useCallback } from 'react';
import useSession from '@hooks/useSession';
import { getRuntimeEnv } from '@utils/runtimeEnv';
import { appInsights } from '../appInsights';

const API_BASE = getRuntimeEnv('REACT_APP_OATT_API_BASE') ||
  `${getRuntimeEnv('REACT_APP_SERVER_PROTOCOL')}://${getRuntimeEnv('REACT_APP_SERVER_HOST')}/api`;

// ── Types ─────────────────────────────────────────────────────────────

export interface ConfigUploadSummary {
  vendorsInserted: number;
  vendorsSkipped: number;
  glInserted: number;
  glSkipped: number;
  metersInserted: number;
  metersSkipped: number;
  rateContractsInserted: number;
  rateIdUpdated: number;
}

export interface ConfigUploadResult {
  success: boolean;
  summary: ConfigUploadSummary;
  errors: string[];
}

interface ConfigUploadState {
  uploading: boolean;
  result: ConfigUploadResult | null;
  validationError: string | null;
}

// ── Hook ──────────────────────────────────────────────────────────────

export function useConfigUpload() {
  const { accessToken } = useSession();

  const [state, setState] = useState<ConfigUploadState>({
    uploading: false,
    result: null,
    validationError: null,
  });

  const uploadFiles = useCallback(
    async (vendorFile: File, meterFile: File): Promise<boolean> => {
      // Client-side guard
      if (!vendorFile || !meterFile) {
        setState((s) => ({
          ...s,
          validationError: 'Both Vendor and Meter files are required.',
        }));
        return false;
      }

      const isXlsx = (f: File) => f.name.toLowerCase().endsWith('.xlsx');
      if (!isXlsx(vendorFile) || !isXlsx(meterFile)) {
        setState((s) => ({ ...s, validationError: 'Both files must be .xlsx format.' }));
        return false;
      }

      setState({ uploading: true, result: null, validationError: null });

      try {

        if (!accessToken) {
          throw new Error('No access token available. Please log in again.');
        }

        if (!API_BASE) {
          throw new Error('Missing REACT_APP_OATT_API_BASE environment variable');
        }

        const uploadUrl = `${API_BASE}/config-upload`;

        const formData = new FormData();
        formData.append('vendorFile', vendorFile);
        formData.append('meterFile', meterFile);

        const response = await fetch(uploadUrl, {
          method: 'POST',
          body: formData,
          headers: { Authorization: `Bearer ${accessToken}` },
          // Do NOT set Content-Type – browser sets multipart boundary automatically
        });

        const contentType = response.headers.get('content-type') ?? '';
        if (!contentType.includes('application/json')) {
          const text = await response.text();
          throw new Error(`Server error (${response.status}): ${text}`);
        }

        const data: ConfigUploadResult = await response.json();

        if (!response.ok && response.status !== 207) {
          throw new Error(
            (data as any).message || data.errors?.[0] || `Upload failed (${response.status})`
          );
        }

        setState({ uploading: false, result: data, validationError: null });
        appInsights?.trackEvent({
          name: data.success ? 'ConfigUploadSuccess' : 'ConfigUploadFailure',
          properties: { errors: (data.errors ?? []).join(', ') },
        });
        return data.success;
      } catch (err: any) {
        setState({
          uploading: false,
          result: { success: false, summary: emptySummary(), errors: [err.message] },
          validationError: null,
        });
        appInsights?.trackEvent({ name: 'ConfigUploadFailure', properties: { error: err.message } });
        return false;
      }
    },
    [accessToken]
  );

  const reset = useCallback(() => {
    setState({ uploading: false, result: null, validationError: null });
  }, []);

  return { ...state, uploadFiles, reset };
}

// ── Helpers ───────────────────────────────────────────────────────────

function emptySummary(): ConfigUploadSummary {
  return {
    vendorsInserted: 0, vendorsSkipped: 0,
    glInserted: 0, glSkipped: 0,
    metersInserted: 0, metersSkipped: 0,
    rateContractsInserted: 0, rateIdUpdated: 0,
  };
}